import { faChevronDown } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@pickleballinc/ui/components/ui/select';
import { useParams, usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect } from 'react';

import { useSeasonDistrictDivisionInfo } from '@/hooks/custom-hooks/useSeasonDistrictDivisionInfo';
import { useTeamLeagueInfo } from '@/hooks/custom-hooks/useTeamLeagueInfo';
import { useTeamsOptionsQuery } from '@/hooks/teams/useTeamsOptionsQuery';

interface PlayerDetailsPublicPageFiltersProps {
	showSeasonFilter?: boolean;
	showTeamFilter?: boolean;
	seasonValue?: string;
	teamValue?: string;
	onSeasonChange?: (season: string | null) => void;
	onTeamChange?: (option: string | null) => void;
}

export const PlayerDetailsPublicPageFilters = ({
	showSeasonFilter = true,
	showTeamFilter = true,
	seasonValue,
	teamValue,
	onSeasonChange,
	onTeamChange
}: PlayerDetailsPublicPageFiltersProps) => {
	const searchParams = useSearchParams();
	const router = useRouter();
	const pathname = usePathname();
	const params = useParams<{ playerId: string }>();
	const { seasonOptions, firstSeasonOptionUuid } = useSeasonDistrictDivisionInfo();
	const teamLeagueInfo = useTeamLeagueInfo();

	const { data: teams } = useTeamsOptionsQuery({ seasonUuid: seasonValue, playerUuid: params.playerId });

	const teamOptions = teams?.data || [];
	const seasonUuid = teamLeagueInfo.seasonUuid || firstSeasonOptionUuid;

	useEffect(() => {
		const params = new URLSearchParams(searchParams.toString());

		if (showSeasonFilter && !params.get('season') && seasonUuid) {
			params.set('season', seasonUuid);
		}
		if (showTeamFilter && !params.get('team') && teams?.data?.[0]) {
			params.set('team', teams.data[0].value);
		}

		if (params.toString() !== searchParams.toString()) {
			router.replace(`?${params.toString()}`, { scroll: false });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [seasonOptions, teams?.data, pathname, seasonUuid]);

	return (
		<div className="flex w-full gap-6 pt-6">
			{showSeasonFilter ? (
				<div className="w-1/2 mb-4 md:mb-6">
					<p className="text-sm font-medium leading-5 text-gray-700">Select season</p>
					<Select onValueChange={(value) => onSeasonChange?.(value)} value={seasonValue || seasonUuid}>
						<SelectTrigger
							className="bg-white focus:ring-0 mt-1.5 !max-w-[630px]"
							icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
						>
							<SelectValue placeholder="Select season" />
						</SelectTrigger>
						<SelectContent>
							{seasonOptions.map((season) => (
								<SelectItem key={season.value} value={season.value}>
									{season.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			) : null}

			{showTeamFilter ? (
				<div className="w-1/2 mb-4 md:mb-6">
					<p className="text-sm font-medium leading-5 text-gray-700">Select team</p>
					<Select onValueChange={(value) => onTeamChange?.(value)} value={teamValue || teamOptions[0]?.value || ''}>
						<SelectTrigger
							className="bg-white focus:ring-0 mt-1.5 !max-w-[630px]"
							icon={({ className }: { className: string }) => <FontAwesomeIcon icon={faChevronDown} className={className} />}
						>
							<SelectValue placeholder="Select team" />
						</SelectTrigger>
						<SelectContent>
							{teamOptions.length === 0 ? (
								<SelectItem value="no_options" disabled>
									No Options
								</SelectItem>
							) : (
								teamOptions.map((team) => (
									<SelectItem key={team.value} value={team.value}>
										{team.label}
									</SelectItem>
								))
							)}
						</SelectContent>
					</Select>
				</div>
			) : null}
		</div>
	);
};
